import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, TextField, InputAdornment, IconButton, FormControlLabel, Checkbox, Button } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import AventiqoLoginbackground22 from "../../assets/images/AventiqoLoginbackground22.jpg";
import AventiqoSmallLogo from "../../assets/images/AventiqoSmallLogo.png";
import AventiqoLOGO from "../../assets/images/AventiqoLOGO.png";
import Loader from '../../common/spinner';
import { postData, getData } from "../../services/axiosInstance";
import { API_ENDPOINTS } from "../../services/endpoints";
import { setAuthData } from "../../utils/auth";

function LoginPage() {
    const navigate = useNavigate();
    const [creds, setCreds] = useState({ email: "", password: "" });
    const [ui, setUi] = useState({
        loading: false,
        error: null,
        showPass: false,
        remember: false
    });

    /**
     * Helper function to handle role-based navigation
     * Wrapped in useCallback to prevent unnecessary effect triggers and fix warnings
     */
    const redirectByRole = useCallback((user) => {
        const userRole = user.user_type.toLowerCase().replace(/\s+/g, '-');
        const allowedRoles = ["manager", "hr", "employee", "team-lead", "approver"];

        if (allowedRoles.includes(userRole)) {
            navigate(`/${userRole}/dashboard`);
        } else {
            navigate("/dashboard");
        }
    }, [navigate]);

    /**
     * LOGIC: AUTOMATIC RECOGNITION
     */
    useEffect(() => {
        const checkAuth = async () => {
            const persistentAuth = localStorage.getItem("persistentAuth");
            if (persistentAuth) {
                try {
                    const { token, user } = JSON.parse(persistentAuth);
                    // 1. Re-hydrate the auth utility/context
                    setAuthData({ token, user });

                    // 2. Validate token by fetching user details
                    try {
                        await getData(`${API_ENDPOINTS.fetchUserDetails}?userId=${user.id}`);
                        // If successful, perform Automatic Redirect
                        redirectByRole(user);
                    } catch (error) {
                        // If token is invalid, clear persistent auth
                        localStorage.removeItem("persistentAuth");
                    }
                } catch (e) {
                    // If data is corrupted, clear it
                    localStorage.removeItem("persistentAuth");
                }
            }
        };
        checkAuth();
    }, [redirectByRole]);

    const inputStyle = {
        mb: 2,
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            borderRadius: '4px',
            color: '#fff',
            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
            '&:hover fieldset': { borderColor: '#2563EB' },
            '&.Mui-focused fieldset': { borderColor: '#2563EB' },
        },
        '& .MuiInputBase-input::placeholder': {
            color: '#8a8f98',
            opacity: 1,
        }
    };

    const handleLogin = async () => {
        // Validation
        if (!creds.email || !creds.password) {
            setUi(prev => ({ ...prev, error: "Please enter both Email and Password" }));
            return;
        }

        setUi(prev => ({ ...prev, loading: true, error: null }));

        try {
            const response = await postData(API_ENDPOINTS.userLogin, {
                email: creds.email.trim().toLowerCase(),
                password: creds.password
            });

            if (response.status === "SUCCESS") {
                const { accessToken, user } = response.message;

                /**
                 * LOGIC: PERSISTENCE
                 */
                if (ui.remember) {
                    localStorage.setItem("persistentAuth", JSON.stringify({
                        token: accessToken,
                        user: user
                    }));
                } else {
                    localStorage.removeItem("persistentAuth");
                }

                // Normal auth flow
                setAuthData({ token: accessToken, user: user });
                redirectByRole(user);

            } else {
                setUi(prev => ({ ...prev, error: response.message?.msg || "Invalid credentials" }));
            }
        } catch (err) {
            const errorMsg = err.response?.data?.message?.msg ||
                err.response?.data?.message ||
                "Server connection failed. Please try again.";

            setUi(prev => ({ ...prev, error: errorMsg }));
        } finally {
            setUi(prev => ({ ...prev, loading: false }));
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                height: "100vh",
                width: "100vw",
                backgroundColor: "#0A0A0F",
                backgroundImage: {
                    xs: "none",
                    md: `linear-gradient(to right, rgba(10,10,15,0) 0%, rgba(10,10,15,0.65) 42%, #0A0A0F 62%), url(${AventiqoLoginbackground22})`,
                },
                backgroundSize: "contain, contain",
                backgroundPosition: "center, left center",
                backgroundRepeat: "no-repeat, no-repeat",
                position: "relative",
            }}
        >

            {ui.loading && <Loader />}

            <Box
                sx={{
                    flex: 1,
                    display: { xs: "none", md: "flex" },
                    alignItems: "center",
                    justifyContent: "start",
                   
                    ml: { md: 6, lg: 10  }
                }}
            >
                <Box component="img" src={AventiqoSmallLogo} alt="Aventiqo" sx={{ width: 150 }} />
            </Box>

            <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", p: 4 }}>
                <Box
                    sx={{
                        width: "90%",
                        maxWidth: "380px",
                        textAlign: "center",
                        bgcolor: "rgba(10, 10, 15, 0.85)",
                        border: "1px solid rgba(41, 182, 246, 0.35)",
                        borderRadius: 3,
                        boxShadow: "0 0 40px rgba(33, 150, 243, 0.15)",
                        p: 5,
                    }}
                >
                    {/* <Box component="img" src={AventiqoLOGO} alt="Aventiqo" sx={{ width: 200, maxWidth: "100%", mb: 1 }} />

                    <Box sx={{ width: "30%", mx: "auto", borderBottom: "1px solid #EDC30099", mb: 3 }} /> */}

                    <Typography variant="h5" sx={{ fontWeight: "bold", mb: 3, color: "#fff" }}>Account Login</Typography>
                    <Typography variant="body2" sx={{ mb: 4, color: "#bbb" }}>
                        If you are already a member you can login with your user ID and password.
                    </Typography>

                    {ui.error && (
                        <Typography variant="body2" sx={{ mb: 3, color: "#ff6b6b", p: 1, bgcolor: "rgba(255,107,107,0.1)", borderRadius: 1 }}>
                            {ui.error}
                        </Typography>
                    )}

                    <TextField
                        fullWidth
                        size="small"
                        placeholder="User ID / Email"
                        value={creds.email}
                        onChange={e => setCreds(prev => ({ ...prev, email: e.target.value }))}
                        sx={inputStyle}
                    />

                    <TextField
                        fullWidth
                        size="small"
                        type={ui.showPass ? "text" : "password"}
                        placeholder="Password"
                        value={creds.password}
                        onChange={e => setCreds(prev => ({ ...prev, password: e.target.value }))}
                        onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                        sx={inputStyle}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setUi(prev => ({ ...prev, showPass: !prev.showPass }))}
                                        edge="end"
                                        sx={{ color: "#bbb" }}
                                    >
                                        {ui.showPass ? <VisibilityOff sx={{ fontSize: 22 }} /> : <Visibility sx={{ fontSize: 22 }} />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={ui.remember}
                                onChange={e => setUi(prev => ({ ...prev, remember: e.target.checked }))}
                                sx={{ color: "#fff", '&.Mui-checked': { color: "#fff" } }}
                            />
                        }
                        label={<Typography variant="caption" sx={{ color: "#bbb" }}>Remember me</Typography>}
                        sx={{ mb: 2, width: '100%', display: 'flex', justifyContent: 'flex-start' }}
                    />

                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleLogin}
                        disabled={ui.loading}
                        sx={{
                            background: "linear-gradient(90deg, #2563EB 0%, #06B6D4 100%)",
                            border: "1px solid #265BE480",
                            fontWeight: "bold",
                            "&:hover": { background: "linear-gradient(90deg, #1d4fc4, #0596ab)" },
                            textTransform: 'none',
                            height: '40px'
                        }}
                    >
                        {ui.loading ? "Logging in..." : "Login"}
                    </Button>

                    <Typography
                        variant="caption"
                        sx={{
                            mt: 2,
                            color: "#00AAC1",
                            cursor: "pointer",
                            display: "flex",
                            justifyContent: "flex-start",
                            "&:hover": { color: "#fff", textDecoration: "underline" }
                        }}
                        onClick={() => navigate("/forgot-password")}
                    >
                        Forgot Password?
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

export default LoginPage;


// const handleLogin = async () => {
//         // Validation
//         if (!creds.email || !creds.password) {
//             setUi(prev => ({ ...prev, error: "Please enter both Email and Password" }));
//             return;
//         }

//         setUi(prev => ({ ...prev, loading: true, error: null }));

//         try {
//             const response = await postData(API_ENDPOINTS.userLogin, {
//                 identifier: creds.email.trim().toLowerCase(),
//                 password: creds.password
//             });

//             // Updated check: API now returns response.status and response.result
//             if (response.status === "SUCCESS") {
//                 const { accessToken, user } = response.result; // Changed from response.message

//                 /**
//                  * LOGIC: PERSISTENCE
//                  */
//                 if (ui.remember) {
//                     localStorage.setItem("persistentAuth", JSON.stringify({
//                         token: accessToken,
//                         user: user
//                     }));
//                 } else {
//                     localStorage.removeItem("persistentAuth");
//                 }

//                 // Normal auth flow
//                 setAuthData({ token: accessToken, user: user });
//                 redirectByRole(user);

//             } else {
//                 // Fallback for failed status using the new message string location
//                 setUi(prev => ({ ...prev, error: response.message || "Invalid credentials" }));
//             }
//         } catch (err) {
//             // Updated error handling to look for the message string in the response
//             const errorMsg = err.response?.data?.message ||
//                 "Server connection failed. Please try again.";

//             setUi(prev => ({ ...prev, error: errorMsg }));
//         } finally {
//             setUi(prev => ({ ...prev, loading: false }));
//         }
//     };